/* Navigation
   ========================================================================== */
// eslint-disable-next-line import/no-extraneous-dependencies
import A11yDialog from 'a11y-dialog';
// Dialog Menu
export const initDialogMenu = () => {
    let dialogNavigation = null;
    const mql = window.matchMedia('(min-width: 1024px)');
    // Clean up dialog attributes & reset state
    const cleanUpDialogAttributes = (elementId, removeOverflow = false) => {
        const element = document.getElementById(elementId);
        if (!element)
            return;
        if (removeOverflow) {
            document.documentElement.classList.remove('overflow-hidden', 'is-nav-open');
            document.body.classList.remove('overflow-hidden');
        }
        ['role', 'aria-modal', 'tabindex', 'aria-hidden'].forEach(attr => element.removeAttribute(attr));
        element.setAttribute('data-state', 'open');
    };
    const destroyAllDialogs = () => {
        // Destroy main navigation dialog
        if (dialogNavigation) {
            dialogNavigation.destroy();
            dialogNavigation = null;
        }
        // Destroy all submenu dialogs
        const submenuDialogs = document.querySelectorAll('[id^="submenu-dialog-"]');
        submenuDialogs.forEach((element) => {
            const el = element;
            if (el.submenuDialogInstance) {
                el.submenuDialogInstance.destroy();
                delete el.submenuDialogInstance;
            }
        });
    };
    const initDialogs = () => {
        const mainNavigation = document.getElementById('main-navigation-container');
        if (!mainNavigation)
            return;
        if (!dialogNavigation) {
            dialogNavigation = new A11yDialog(mainNavigation);
            dialogNavigation.on('show', () => {
                // Save scroll position before opening the menu
                const scrollY = window.scrollY;
                // Prevent body from scrolling
                document.documentElement.classList.add('overflow-hidden', 'is-nav-open');
                document.body.classList.add('overflow-hidden');
                document.body.style.position = 'fixed';
                document.body.style.top = `-${scrollY}px`;
                mainNavigation.setAttribute('data-state', 'open');
                // Reset scroll position on open (not on close, because of CSS transitions)
                const scrollableAreas = document.querySelectorAll('#main-navigation-container [data-scroll-container]');
                scrollableAreas.forEach((el) => {
                    el.scrollTop = 0;
                });
            });
            dialogNavigation.on('hide', () => {
                // Restore scroll position
                const scrollY = parseInt(document.body.style.top || '0', 10);
                // Restore body position and scroll position
                document.documentElement.classList.remove('overflow-hidden', 'is-nav-open');
                document.body.classList.remove('overflow-hidden');
                document.body.style.position = '';
                document.body.style.top = '';
                window.scrollTo(0, Math.abs(scrollY));
                mainNavigation.setAttribute('data-state', 'closed');
            });
            dialogNavigation.on('destroy', () => {
                cleanUpDialogAttributes('main-navigation-container', true);
            });
        }
        // Init submenu dialogs
        const submenuDialogs = document.querySelectorAll('[id^="submenu-dialog-"]');
        submenuDialogs.forEach((element) => {
            const el = element;
            if (!el.submenuDialogInstance) {
                const submenuDialog = new A11yDialog(el);
                submenuDialog.on('show', () => {
                    el.setAttribute('data-state', 'open');
                });
                submenuDialog.on('hide', () => {
                    el.setAttribute('data-state', 'closed');
                    // Only reset this submenu's scroll, not the main menu
                    const scrollableArea = el.querySelector('[data-scroll-container]');
                    if (scrollableArea)
                        scrollableArea.scrollTop = 0;
                });
                submenuDialog.on('destroy', () => {
                    cleanUpDialogAttributes(element.id);
                });
                el.submenuDialogInstance = submenuDialog;
            }
        });
    };
    const handleMediaChange = () => {
        // On desktop destroy all dialogs
        if (mql.matches) {
            destroyAllDialogs();
        }
        else {
            initDialogs();
        }
    };
    // Initial check
    handleMediaChange();
    // Watch for changes
    mql.addEventListener('change', handleMediaChange);
};
// Sticky Header
export const initStickyHeader = () => {
    const header = document.querySelector('.header');
    if (!header)
        return;
    const mql = window.matchMedia('(min-width: 1024px)');
    const SCROLL_UP_THRESHOLD = 10;
    const isCoverHeader = header.classList.contains('header--desktop-cover');
    const headerHeight = header.offsetHeight;
    const popins = document.getElementById('popins');
    let cachedBannerHeight = 0;
    let lastScrollY = window.scrollY;
    let lastToggleScrollY = lastScrollY;
    let ticking = false;
    const getBannerHeight = () => {
        if (!popins?.classList.contains('is-banner-show'))
            return 0;
        if (!cachedBannerHeight) {
            const banner = document.getElementById('dialog-banner');
            cachedBannerHeight = banner?.offsetHeight ?? 0;
        }
        return cachedBannerHeight;
    };
    const handleScroll = () => {
        // breakpoint may have changed between rAF scheduling and execution
        if (!mql.matches) {
            ticking = false;
            return;
        }
        const currentScrollY = Math.max(0, window.scrollY);
        const delta = currentScrollY - lastScrollY;
        // Cover header: scroll-link --header-top to follow the banner out.
        if (isCoverHeader) {
            const bannerHeight = getBannerHeight();
            if (bannerHeight > 0) {
                header.style.setProperty('--header-top', `${Math.max(0, bannerHeight - currentScrollY)}px`);
            }
            else {
                header.style.removeProperty('--header-top');
            }
        }
        // Visibility logic
        if (currentScrollY <= headerHeight) {
            header.classList.remove('header--visible', 'header--invisible');
            lastToggleScrollY = currentScrollY;
        }
        else if (delta > 0) {
            header.classList.add('header--invisible');
            header.classList.remove('header--visible');
            lastToggleScrollY = currentScrollY;
        }
        else if (lastToggleScrollY - currentScrollY >= SCROLL_UP_THRESHOLD) {
            header.classList.remove('header--invisible');
            header.classList.add('header--visible');
            lastToggleScrollY = currentScrollY;
        }
        lastScrollY = currentScrollY;
        ticking = false;
    };
    const onScroll = () => {
        if (!ticking && mql.matches) {
            window.requestAnimationFrame(handleScroll);
            ticking = true;
        }
    };
    const handleMediaChange = () => {
        if (!mql.matches) {
            header.classList.remove('header--visible', 'header--invisible');
            header.style.removeProperty('--header-top');
        }
        else {
            handleScroll();
        }
        lastScrollY = window.scrollY;
        lastToggleScrollY = lastScrollY;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    mql.addEventListener('change', handleMediaChange);
    // Sync immediately when the banner opens/closes (async after initPopins),
    if (popins) {
        new MutationObserver(() => {
            if (mql.matches)
                handleScroll();
        }).observe(popins, { attributeFilter: ['class'] });
    }
    handleMediaChange();
};
export const initNavigationOverflow = () => {
    const mql = window.matchMedia("(min-width: 1024px)");
    const checkNavigationOverflow = () => {
        const navigation = document.getElementById("main-navigation");
        const menubar = navigation?.querySelector('ul');
        if (!navigation || !menubar)
            return;
        const items = Array.from(menubar.children);
        if (items.length === 0)
            return;
        // Mobile/tablet show all items
        if (!mql.matches) {
            items.forEach(item => item.classList.remove("hidden"));
            return;
        }
        const navWidth = navigation.clientWidth;
        let currentWidth = 0;
        // Reset items visibility and calculate in one pass
        for (let i = 0; i < items.length; i += 1) {
            const item = items[i];
            item.classList.remove("hidden");
            const itemWidth = item.offsetWidth;
            const totalWidth = currentWidth + itemWidth;
            if (totalWidth <= navWidth) {
                currentWidth = totalWidth;
            }
            else {
                // Hide remaining items after this one
                for (let j = i; j < items.length; j += 1) {
                    items[j].classList.add("hidden");
                }
                break;
            }
        }
    };
    // Event listeners
    window.addEventListener("resize", () => requestAnimationFrame(checkNavigationOverflow), { passive: true });
    mql.addEventListener("change", checkNavigationOverflow);
    // Initial check after fonts
    if (document.fonts?.ready) {
        document.fonts.ready.then(checkNavigationOverflow);
    }
    else {
        window.addEventListener("load", checkNavigationOverflow, { once: true });
    }
};
